/**
 * 形状目录 + 「包围盒 → 目标形状几何」的唯一实现。
 *
 * 语义（产品口径，两档可切换）：
 *  · `bbox`（默认）**保持包围盒**：新形状内接在原来的框里，中心不动。
 *    这就是 matplotlib 定义 marker 的口径（三角 `^` 的顶点落在包围盒四角），
 *    所以散点标记互换时视觉重量差得多（三角只有矩形面积的一半）—— 但位置与「尺寸」含义不变。
 *  · `area` **保持面积**：把新形状等比缩放到与原来面积相等（中心不动），
 *    换形状前后「墨量」接近，适合本来就用面积编码变量（气泡图）的图。
 *
 * 所有坐标都在**元素自身的用户坐标系**里（和 localBBox 同一空间）→ 调用方原样保留 transform 即可。
 */
import type { Pt, Rect } from './geom/matrix';
import { polygonArea } from './geom/local-bbox';

export type ShapeKind =
  | 'rect'
  | 'roundRect'
  | 'circle'
  | 'ellipse'
  | 'triangle'
  | 'triangleDown'
  | 'diamond'
  | 'pentagon'
  | 'hexagon'
  | 'star'
  | 'plus'
  | 'cross';

export interface ShapeDef {
  kind: ShapeKind;
  label: string;
}

/** 面板里可选的形状（顺序 = 网格里的顺序） */
export const SHAPE_LIST: readonly ShapeDef[] = [
  { kind: 'rect', label: '矩形' },
  { kind: 'roundRect', label: '圆角矩形' },
  { kind: 'circle', label: '圆' },
  { kind: 'ellipse', label: '椭圆' },
  { kind: 'triangle', label: '三角' },
  { kind: 'triangleDown', label: '倒三角' },
  { kind: 'diamond', label: '菱形' },
  { kind: 'pentagon', label: '五边形' },
  { kind: 'hexagon', label: '六边形' },
  { kind: 'star', label: '星形' },
  { kind: 'plus', label: '十字' },
  { kind: 'cross', label: '叉号' },
];

export const SHAPE_LABEL: Record<ShapeKind, string> = SHAPE_LIST.reduce(
  (acc, def) => {
    acc[def.kind] = def.label;
    return acc;
  },
  {} as Record<ShapeKind, string>,
);

/**
 * 形状图标（16×16 描边路径的 `d`）。
 * 与形状目录放在一起：**一处定义，两个面板共用**（形状工作台浮层 / 属性面板的形状区），
 * 以后加形状只改 SHAPE_LIST 与这里两行，不会出现两个面板图标对不上的情况。
 */
export const SHAPE_ICON_D: Record<ShapeKind, string> = {
  rect: 'M2.5 3.5h11v9h-11z',
  roundRect: 'M4.9 3.5h6.2a2.4 2.4 0 0 1 2.4 2.4v4.2a2.4 2.4 0 0 1-2.4 2.4H4.9a2.4 2.4 0 0 1-2.4-2.4V5.9a2.4 2.4 0 0 1 2.4-2.4z',
  circle: 'M13 8a5 5 0 1 1-10 0 5 5 0 0 1 10 0z',
  ellipse: 'M13.6 8c0 2.1-2.5 3.8-5.6 3.8S2.4 10.1 2.4 8 4.9 4.2 8 4.2 13.6 5.9 13.6 8z',
  triangle: 'M8 2.5 14 13.5H2z',
  triangleDown: 'M8 13.5 2 2.5h12z',
  diamond: 'M8 2.2 14 8 8 13.8 2 8z',
  pentagon: 'M8 2.2 14 6.7l-2.3 7H4.3L2 6.7z',
  hexagon: 'M5.2 2.6h5.6L13.6 8l-2.8 5.4H5.2L2.4 8z',
  star: 'M8 1.8l1.7 4.3 4.6.3-3.6 2.9 1.2 4.4L8 11.2l-3.9 2.5 1.2-4.4L1.7 6.4l4.6-.3z',
  plus: 'M6.5 2.2h3v4.3h4.3v3H9.5v4.3h-3V9.5H2.2v-3h4.3z',
  cross: 'M3.6 4.9 4.9 3.6 8 6.6l3.1-3 1.3 1.3L9.4 8l3 3.1-1.3 1.3L8 9.4l-3.1 3-1.3-1.3L6.6 8z',
};

export type FitMode = 'bbox' | 'area';

export interface ShapeGeometry {
  tag: string;
  attrs: Record<string, string>;
}

const r2 = (n: number): number => {
  if (!Number.isFinite(n)) return 0;
  const v = Math.round(n * 100) / 100;
  return v === 0 ? 0 : v; // 去掉 -0（写进属性会变成 "0" 之外看着像负零）
};

const fmt = (n: number): string => String(r2(n));

const pointsAttr = (pts: readonly Pt[]): string => pts.map((p) => `${fmt(p.x)},${fmt(p.y)}`).join(' ');

/** 正多边形（n 个顶点内接于椭圆框） */
function regularPolygon(cx: number, cy: number, rx: number, ry: number, n: number, startDeg = -90): Pt[] {
  const out: Pt[] = [];
  for (let i = 0; i < n; i++) {
    const a = ((startDeg + (360 * i) / n) * Math.PI) / 180;
    out.push({ x: cx + rx * Math.cos(a), y: cy + ry * Math.sin(a) });
  }
  return out;
}

/** 星形（2n 顶点，内外半径交替） */
function starPolygon(cx: number, cy: number, rx: number, ry: number, n: number, inner = 0.382): Pt[] {
  const out: Pt[] = [];
  for (let i = 0; i < n * 2; i++) {
    const k = i % 2 === 0 ? 1 : inner;
    const a = ((-90 + (180 * i) / n) * Math.PI) / 180;
    out.push({ x: cx + rx * k * Math.cos(a), y: cy + ry * k * Math.sin(a) });
  }
  return out;
}

/**
 * 十字（＋）与叉号（×）：由 `min(w,h)/3` 的臂宽生成 12 顶点轮廓。
 * 用多边形而不是两条线 —— 填充、描边、渐变的语义与其它形状一致。
 */
function armPolygon(cx: number, cy: number, w: number, h: number, rotated: boolean): Pt[] {
  const t = Math.min(w, h) / 3;
  const hw = w / 2;
  const hh = h / 2;
  const ht = t / 2;
  // 以中心为原点的十字顶点（顺时针）
  const pts: Array<[number, number]> = [
    [-ht, -hh],
    [ht, -hh],
    [ht, -ht],
    [hw, -ht],
    [hw, ht],
    [ht, ht],
    [ht, hh],
    [-ht, hh],
    [-ht, ht],
    [-hw, ht],
    [-hw, -ht],
    [-ht, -ht],
  ];
  if (!rotated) return pts.map(([x, y]) => ({ x: cx + x, y: cy + y }));
  // 叉号 = 十字旋转 45°：旋转后按包围盒等比夹回（不然会超出原框 √2 倍）
  const a = Math.PI / 4;
  const c = Math.cos(a);
  const s = Math.sin(a);
  const rot = pts.map(([x, y]) => ({ x: x * c - y * s, y: x * s + y * c }));
  const rw = Math.max(...rot.map((p) => Math.abs(p.x)));
  const rh = Math.max(...rot.map((p) => Math.abs(p.y)));
  const scale = Math.min(hw / rw, hh / rh);
  return rot.map((p) => ({ x: cx + p.x * scale, y: cy + p.y * scale }));
}

/**
 * 把一组点**按轴拉伸到刚好填满目标框**（中心不动）。
 * 为什么必须做这一步：正五边形内接于椭圆时，它的包围盒比框小（宽 0.95×、高 0.90×），
 * 而「保持包围盒」的语义要求新形状的框**逐位等于**原框（matplotlib 的 marker 就是按单位框定义的）。
 */
function fitPointsToBox(pts: readonly Pt[], box: Rect): Pt[] {
  if (pts.length === 0) return [];
  const xs = pts.map((p) => p.x);
  const ys = pts.map((p) => p.y);
  const bx = Math.min(...xs);
  const by = Math.min(...ys);
  const bw = Math.max(...xs) - bx;
  const bh = Math.max(...ys) - by;
  if (bw <= 0 || bh <= 0) return [...pts];
  const sx = box.w / bw;
  const sy = box.h / bh;
  return pts.map((p) => ({ x: box.x + (p.x - bx) * sx, y: box.y + (p.y - by) * sy }));
}

/** 生成物的面积（`area` 口径的换算依据；算不出返回 null） */
export function geometryArea(geo: ShapeGeometry): number | null {
  const attrs = geo.attrs;
  const n = (name: string): number => Number.parseFloat(attrs[name] ?? '');
  switch (geo.tag) {
    case 'rect':
      return Number.isFinite(n('width')) && Number.isFinite(n('height')) ? Math.abs(n('width') * n('height')) : null;
    case 'circle': {
      const r = n('r');
      return Number.isFinite(r) ? Math.PI * r * r : null;
    }
    case 'ellipse': {
      const rx = n('rx');
      const ry = n('ry');
      return Number.isFinite(rx) && Number.isFinite(ry) ? Math.PI * Math.abs(rx * ry) : null;
    }
    case 'polygon': {
      const pts: Pt[] = (attrs['points'] ?? '')
        .split(/\s+/)
        .filter(Boolean)
        .map((pair) => {
          const [x, y] = pair.split(',').map(Number);
          return { x: x ?? 0, y: y ?? 0 };
        });
      const area = polygonArea(pts);
      return area > 0 ? area : null;
    }
    default:
      return null;
  }
}

/**
 * 包围盒 + 形状 → 目标标签与几何属性（本地坐标系）。
 *
 * ⚠️ 返回 null 只有一种情况：包围盒退化（宽或高为 0，例如水平/垂直直线）——
 * 那种元素没法谈「内接」，调用方应跳过它并在列表里说明原因。
 */
export function shapeGeometry(kind: ShapeKind, box: Rect, opts: { fit?: FitMode; sourceArea?: number | null } = {}): ShapeGeometry | null {
  const { w, h } = box;
  if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return null;

  const build = (useBox: Rect): ShapeGeometry => {
    const { x, y, w: bw, h: bh } = useBox;
    const cx = x + bw / 2;
    const cy = y + bh / 2;
    const rx = bw / 2;
    const ry = bh / 2;
    /** 多边形统一出口：生成后一律拉伸到刚好填满目标框（对三角/菱形是无副作用的恒等操作） */
    const poly = (pts: readonly Pt[]): ShapeGeometry => ({
      tag: 'polygon',
      attrs: { points: pointsAttr(fitPointsToBox(pts, useBox)) },
    });

    switch (kind) {
      case 'rect':
        return { tag: 'rect', attrs: { x: fmt(x), y: fmt(y), width: fmt(bw), height: fmt(bh) } };
      case 'roundRect':
        return {
          tag: 'rect',
          attrs: { x: fmt(x), y: fmt(y), width: fmt(bw), height: fmt(bh), rx: fmt(Math.min(bw, bh) * 0.18) },
        };
      case 'circle':
        return { tag: 'circle', attrs: { cx: fmt(cx), cy: fmt(cy), r: fmt(Math.min(bw, bh) / 2) } };
      case 'ellipse':
        return { tag: 'ellipse', attrs: { cx: fmt(cx), cy: fmt(cy), rx: fmt(rx), ry: fmt(ry) } };
      case 'triangle':
        return poly([{ x: cx, y }, { x: x + bw, y: y + bh }, { x, y: y + bh }]);
      case 'triangleDown':
        return poly([{ x, y }, { x: x + bw, y }, { x: cx, y: y + bh }]);
      case 'diamond':
        return poly([{ x: cx, y }, { x: x + bw, y: cy }, { x: cx, y: y + bh }, { x, y: cy }]);
      case 'pentagon':
        return poly(regularPolygon(cx, cy, rx, ry, 5));
      case 'hexagon':
        return poly(regularPolygon(cx, cy, rx, ry, 6));
      case 'star':
        return poly(starPolygon(cx, cy, rx, ry, 5));
      case 'plus':
        return poly(armPolygon(cx, cy, bw, bh, false));
      case 'cross':
        return poly(armPolygon(cx, cy, bw, bh, true));
      default:
        return { tag: 'rect', attrs: { x: fmt(x), y: fmt(y), width: fmt(bw), height: fmt(bh) } };
    }
  };

  const geo = build(box);
  if (opts.fit !== 'area') return geo;

  // 保持面积：面积按 k² 缩放 → k = sqrt(原面积 / 新形状面积)，再以同一个中心生成一次
  const source = opts.sourceArea;
  const target = geometryArea(geo);
  if (!source || !target || source <= 0 || target <= 0) return geo;
  const k = Math.sqrt(source / target);
  if (!Number.isFinite(k)) return geo;
  const clamped = Math.min(4, Math.max(0.25, k));
  if (Math.abs(clamped - 1) < 1e-6) return geo;
  const cx = box.x + w / 2;
  const cy = box.y + h / 2;
  const scaled: Rect = { x: cx - (w * clamped) / 2, y: cy - (h * clamped) / 2, w: w * clamped, h: h * clamped };
  return build(scaled);
}
